import _ from 'lodash';
import { useCallback, useState } from 'react';
import { calculateVersionedName, getVersionNumberFromLabel } from '@/accessControl/itemAclModal.utilities';
import { internalVersioningApi } from '@/accessControl/versioning.api';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { VersionHistoryOperations } from '@/core/hooks/useVersionState.hook';
import { unsubscribeFromLockingChannel } from '@/main/cleanup.utilities';
import { getViewWorksheetLink } from '@/main/routing.utilities';
import { emitWorkbook, emitWorkbooks } from '@/services/notifier.service';
import { doTrack } from '@/track/track.service';
import { logError } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import { subscribeToLockingChannel } from '@/utilities/socket.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { setLockingErrors, setWorkbookDisplayMode, setWorkbookName } from '@/workbook/workbook.actions';
import { WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import { sqAuditApi } from '@seeqdev/api/endpoint/AuditApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import type { VersionState } from '@/core/hooks/useVersionState.hook';
import type { LoadOptions } from '@/worksheet/worksheet.types';
import type { LockErrorOutputV1 } from '@seeqdev/api/model/LockErrorOutputV1';
import type { LockErrorPathOutputV1 } from '@seeqdev/api/model/LockErrorPathOutputV1';

interface UseLockAndVersionOptions<T> {
  itemId: string;
  /** Called when locking succeeds (before redirect). Use to close the containing modal. */
  closeModal: () => void;
  /** Optional async work to perform after lock+version but before the page redirect. */
  onBeforeRedirect?: () => Promise<T>;
}

export function useLockAndVersion<T>({ itemId, closeModal, onBeforeRedirect }: UseLockAndVersionOptions<T>) {
  const [lockErrors, setLockErrors] = useState<LockErrorOutputV1[] | undefined>(undefined);
  const [hasLockingFailedPreviouslyDueToGlobalItems, setHasLockingFailedPreviouslyDueToGlobalItems] = useState(false);

  const { stateParams } = useFluxPaths(sqWorkbenchStore, ['stateParams']);
  const { name: workbookName, isTopic, isVantage } = useFluxPaths(sqWorkbookStore, ['name', 'isTopic', 'isVantage']);

  const handleLockError = useCallback(
    (response: unknown, workbookId: string, worksheetId: string) => {
      const data = (response as { data?: { errors?: LockErrorOutputV1[] } })?.data;
      if (data?.errors) {
        setHasLockingFailedPreviouslyDueToGlobalItems(true);
        errorToast({ messageKey: 'ACCESS_CONTROL.LOCKING.LOCK_FAILURE' });
        setLockErrors(data.errors);
        doTrack('Locking/Versioning', isVantage ? 'Lock Vantage' : isTopic ? 'Lock Topic' : 'Lock Analysis', {
          lockedSuccessfully: false,
          numErrors: data.errors.length ?? 0,
          workbookId,
        });

        if (data.errors.length > 0) {
          const globalItemsWithMergedItemPaths: LockErrorOutputV1[] = _.chain(data.errors)
            .groupBy('id')
            .map((group) => ({
              id: group[0].id,
              item: group[0].item,
              itemPath: group
                .flatMap((g: LockErrorOutputV1) => g.itemPath)
                .filter((path): path is LockErrorPathOutputV1 => path !== undefined),
            }))
            .value();

          setLockingErrors(globalItemsWithMergedItemPaths);
        }
      } else {
        errorToast({ httpResponseOrError: response });
      }
      const loadOptions: LoadOptions = {
        workbookId,
        worksheetId,
        workbookDisplay: WORKBOOK_DISPLAY.EDIT,
      };
      subscribeToLockingChannel(loadOptions);
    },
    [isVantage, isTopic],
  );

  const lockAndVersion = useCallback(
    async (versionState: VersionState) => {
      const { workbookId, worksheetId } = stateParams;
      const originalWorkbookName = workbookName;
      const { versionHistoryOperation, newWorkbookName, selectedVersionHistory, notes } = versionState;

      const lockAndTrack = async (versionNumber: number) => {
        unsubscribeFromLockingChannel();
        await sqItemsApi.lockItem({ id: itemId });
        doTrack('Locking/Versioning', isTopic ? 'Lock and Version Topic' : 'Lock and Version Analysis', {
          lockedSuccessfully: true,
          numErrors: 0,
          workbookId,
          versionNumber,
        });
      };

      const finishAndRedirect = async (toastMessageKey = 'ACCESS_CONTROL.LOCKING.LOCKED_TOAST') => {
        successToast({ messageKey: toastMessageKey });
        closeModal();
        setWorkbookDisplayMode(WORKBOOK_DISPLAY.VIEW);
        if (onBeforeRedirect) {
          await onBeforeRedirect();
        }
        window.location.href = getViewWorksheetLink(workbookId, worksheetId);
      };

      if (isVantage) {
        try {
          unsubscribeFromLockingChannel();
          await sqItemsApi.lockItem({ id: itemId });
          doTrack('Locking/Versioning', 'Lock Vantage', { lockedSuccessfully: true, workbookId });
          emitWorkbooks();
          emitWorkbook(workbookId);
          await finishAndRedirect('ACCESS_CONTROL.LOCKING.LOCKED_VANTAGE_TOAST');
        } catch (err) {
          handleLockError(err, workbookId, worksheetId);
        }
      } else {
        let didPreLockRename = false;

        try {
          if (versionHistoryOperation === VersionHistoryOperations.Create) {
            if (newWorkbookName && newWorkbookName !== originalWorkbookName) {
              await setWorkbookName(workbookId, newWorkbookName, originalWorkbookName);
              didPreLockRename = true;
            }

            const versionNumber = 1;
            const baseName =
              newWorkbookName && newWorkbookName !== originalWorkbookName ? newWorkbookName : originalWorkbookName;
            const finalName = calculateVersionedName(baseName, versionNumber);

            await lockAndTrack(versionNumber);
            await sqAuditApi.createVersionHistory({ itemId, notes });
            await internalVersioningApi.renameForVersioning({ itemId, newName: finalName });

            flux.dispatch('WORKBOOK_SET_PROP', { name: finalName }, PUSH_IGNORE);
            flux.dispatch('WORKBENCH_SET_NAME', { name: finalName, workbookId }, PUSH_IGNORE);
            emitWorkbooks();
            emitWorkbook(workbookId);

            await finishAndRedirect();
          } else if (versionHistoryOperation === VersionHistoryOperations.Update && selectedVersionHistory?.value.id) {
            const versionNumber = getVersionNumberFromLabel(selectedVersionHistory.label);
            const baseName = selectedVersionHistory.value.name;
            const finalName = calculateVersionedName(baseName, versionNumber);

            if (finalName !== originalWorkbookName) {
              await setWorkbookName(workbookId, finalName, originalWorkbookName);
              didPreLockRename = true;
            }

            await lockAndTrack(versionNumber);
            await internalVersioningApi.renameForVersioning({ itemId, newName: finalName });

            flux.dispatch('WORKBOOK_SET_PROP', { name: finalName }, PUSH_IGNORE);
            flux.dispatch('WORKBENCH_SET_NAME', { name: finalName, workbookId }, PUSH_IGNORE);
            emitWorkbooks();
            emitWorkbook(workbookId);

            await sqAuditApi.updateVersionHistory(
              { itemId, notes },
              { versionHistoryId: selectedVersionHistory.value.id },
            );

            await finishAndRedirect();
          } else {
            errorToast({ messageKey: 'ACCESS_CONTROL.LOCKING.VERSIONING.FAILED_NEW_VERSION' });
          }
        } catch (err) {
          if (didPreLockRename) {
            try {
              await setWorkbookName(workbookId, originalWorkbookName, originalWorkbookName);
            } catch (rollbackErr) {
              logError(formatMessage`Failed to rollback workbook name after lock failure: ${rollbackErr}`);
            }
          }
          handleLockError(err, workbookId, worksheetId);
        }
      }
    },
    [stateParams, workbookName, isTopic, isVantage, itemId, closeModal, onBeforeRedirect, handleLockError],
  );

  const dismissLockErrors = useCallback(() => {
    setHasLockingFailedPreviouslyDueToGlobalItems(false);
  }, []);

  return { lockAndVersion, dismissLockErrors, lockErrors, hasLockingFailedPreviouslyDueToGlobalItems };
}
